import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Checkbox,
  Descriptions,
  Divider,
  Drawer,
  Empty,
  Input,
  List,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from './api';
import { formatAmount, formatCvrNumber } from './constants';
import type { Customer, CustomerCvrLink, CustomerInsuranceType, InsuranceType } from './types';

const { Text, Title, Paragraph } = Typography;

type StatusFilter = 'ALL' | Customer['status'];
type OwnerFilter = 'ALL' | 'UNASSIGNED' | string;

const statusLabels: Record<Customer['status'], string> = {
  ACTIVE: 'Aktiv',
  ONBOARDING: 'Onboarding',
  AT_RISK: 'I risiko',
  INACTIVE: 'Opsagt'
};

const statusColors: Record<Customer['status'], string> = {
  ACTIVE: 'green',
  ONBOARDING: 'blue',
  AT_RISK: 'volcano',
  INACTIVE: 'default'
};

const statusFilterOptions = [
  { value: 'ALL', label: 'Alle statuser' },
  { value: 'ACTIVE', label: 'Aktive kunder' },
  { value: 'ONBOARDING', label: 'Onboarding' },
  { value: 'AT_RISK', label: 'I risiko' },
  { value: 'INACTIVE', label: 'Opsagte kunder' }
];

function formatDate(value: string | null | undefined) {
  if (!value) {
    return '—';
  }
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return new Intl.DateTimeFormat('da-DK', { dateStyle: 'medium' }).format(date);
  } catch (_error) {
    return value ?? '—';
  }
}

function ownerLabel(customer: Customer) {
  if (customer.account_owner_name && customer.account_owner_name.trim()) {
    return customer.account_owner_name.trim();
  }
  if (customer.account_owner_email && customer.account_owner_email.trim()) {
    return customer.account_owner_email.trim();
  }
  if (customer.account_owner) {
    return 'Ukendt bruger';
  }
  return '—';
}

function buildContactSummary(customer: Customer) {
  const parts: string[] = [];
  if (customer.primary_contact_name) {
    parts.push(customer.primary_contact_name.trim());
  }
  if (customer.primary_contact_email) {
    parts.push(customer.primary_contact_email.trim());
  }
  if (customer.primary_contact_phone) {
    parts.push(customer.primary_contact_phone.trim());
  }
  return parts.length ? parts.join(' · ') : '—';
}

function getCustomerLabels(customer: Customer) {
  const labels = Array.isArray(customer.labels) ? customer.labels : [];
  return [...labels].sort((a, b) => a.name.localeCompare(b.name, 'da', { sensitivity: 'base' }));
}

function getCustomerCvrLinks(customer: Customer): CustomerCvrLink[] {
  if (customer.cvr_links && customer.cvr_links.length) {
    return customer.cvr_links;
  }
  return [
    {
      id: customer.id,
      customer_id: customer.id,
      cvrnummer: customer.cvrnummer,
      label: null,
      status: customer.status,
      annual_value: customer.annual_value,
      commission_percent: customer.commission_percent,
      commission_amount: customer.commission_amount,
      currency: customer.currency,
      contract_signed_at: customer.contract_signed_at,
      contract_starts_at: customer.contract_starts_at,
      contract_expires_at: customer.contract_expires_at,
      renewal_notice_at: customer.renewal_notice_at,
      notes: null,
      created_at: customer.created_at,
      updated_at: customer.updated_at
    }
  ];
}

function formatCvrLabel(link: CustomerCvrLink, index: number) {
  if (link.label && link.label.trim()) {
    return link.label.trim();
  }
  if (link.cvrnummer) {
    return `CVR ${formatCvrNumber(link.cvrnummer)}`;
  }
  return `Aftale ${index + 1}`;
}

function formatProvisionSummary(
  percent: number | null | undefined,
  amount: number | null | undefined,
  currency?: string | null
) {
  const parts: string[] = [];
  if (percent !== null && percent !== undefined) {
    parts.push(`${percent}%`);
  }
  if (amount !== null && amount !== undefined) {
    parts.push(formatAmount(amount, currency || undefined));
  }
  return parts.length ? parts.join(' · ') : '—';
}

function normalizeInsuranceTypes(items: any[]): CustomerInsuranceType[] {
  return items
    .map((item: any) => ({
      ...item,
      insuranceTypeId: String(
        item.insuranceTypeId ??
          item.insuranceTypeID ??
          item.insurancetypeid ??
          item.insurance_type_id ??
          item.forsikringstype_id ??
          item.forsikringstypeid ??
          item.id ??
          ''
      ),
      title:
        item.title ??
        item.navn ??
        item.name ??
        item.Title ??
        item.insurance_type_title ??
        item.forsikringstype_title ??
        item.forsikringstype_navn ??
        item.forsikringstypenavn ??
        item.insuranceTypeName ??
        item.insurance_type_name ??
        ''
    }))
    .map((item) => ({
      ...item,
      title: item.title || item.insuranceTypeId // ensure something visible
    }))
    .filter((item) => item.insuranceTypeId);
}

function extractInsuranceTypes(payload: any): any[] {
  const candidates = [
    payload?.insurance_types,
    payload?.insuranceTypes,
    payload?.insurance_types?.items,
    payload?.insuranceTypes?.items
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
    if (typeof candidate === 'string') {
      try {
        const parsed = JSON.parse(candidate);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch (_error) {
        // ignore parse errors
      }
    }
  }

  return [];
}

export default function CustomersPage() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ACTIVE');
  const [ownerFilter, setOwnerFilter] = useState<OwnerFilter>('ALL');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [availableInsuranceTypes, setAvailableInsuranceTypes] = useState<InsuranceType[]>([]);
  const [customerInsuranceTypes, setCustomerInsuranceTypes] = useState<CustomerInsuranceType[]>([]);
  const [insuranceTypesLoading, setInsuranceTypesLoading] = useState(false);
  const [insuranceModalOpen, setInsuranceModalOpen] = useState(false);
  const [insuranceModalSaving, setInsuranceModalSaving] = useState(false);
  const [selectedInsuranceTypeIds, setSelectedInsuranceTypeIds] = useState<string[]>([]);
  const [removingInsuranceTypeId, setRemovingInsuranceTypeId] = useState<string | null>(null);
  const isDev = process.env.NODE_ENV !== 'production';

  const inlineInsuranceTypes = useMemo(() => {
    if (!selectedCustomer) return [] as CustomerInsuranceType[];
    return normalizeInsuranceTypes(extractInsuranceTypes(selectedCustomer as any));
  }, [selectedCustomer]);

  const displayInsuranceTypes = useMemo(() => {
    if (customerInsuranceTypes.length > 0) return customerInsuranceTypes;
    if (inlineInsuranceTypes.length > 0) return inlineInsuranceTypes;
    return [] as CustomerInsuranceType[];
  }, [customerInsuranceTypes, inlineInsuranceTypes]);

  useEffect(() => {
    if (!drawerOpen || !selectedCustomer) return;
    if (!isDev) return;
    const rawInline = extractInsuranceTypes(selectedCustomer as any);
    const normalizedInline = normalizeInsuranceTypes(rawInline);
    // Debug info to diagnose missing render in UI
    // eslint-disable-next-line no-console
    console.log('insurance types inline/raw', rawInline);
    // eslint-disable-next-line no-console
    console.log('insurance types inline/normalized', normalizedInline);
    // eslint-disable-next-line no-console
    console.log('insurance types state/customerInsuranceTypes', customerInsuranceTypes);
  }, [drawerOpen, isDev, selectedCustomer, customerInsuranceTypes]);

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'ALL') {
        params.set('status', statusFilter);
      }
      if (ownerFilter !== 'ALL' && ownerFilter !== 'UNASSIGNED') {
        params.set('accountOwner', ownerFilter);
      }
      const query = params.toString() ? `?${params.toString()}` : '';
      const res = await api(`/api/customers${query}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke hente kunder');
      }
      const data = (await res.json()) as Customer[];
      setCustomers(data);
    } catch (error: any) {
      console.error('Failed to load customers', error);
      message.error(error?.message || 'Kunne ikke hente kunder');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, ownerFilter]);

  useEffect(() => {
    void loadCustomers();
  }, [loadCustomers]);

  const ownerOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const customer of customers) {
      if (!customer.account_owner) {
        continue;
      }
      if (!map.has(customer.account_owner)) {
        map.set(customer.account_owner, ownerLabel(customer));
      }
    }
    const entries = Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'da', { sensitivity: 'base' }));
    return [
      { value: 'ALL', label: 'Alle account owners' },
      { value: 'UNASSIGNED', label: 'Uden ansvarlig' },
      ...entries
    ];
  }, [customers]);

  const filteredCustomers = useMemo(() => {
    const normalizedSearch = searchTerm.trim();
    return customers.filter((customer) => {
      if (ownerFilter === 'UNASSIGNED' && customer.account_owner) {
        return false;
      }
      if (ownerFilter !== 'ALL' && ownerFilter !== 'UNASSIGNED' && customer.account_owner !== ownerFilter) {
        return false;
      }
      if (!normalizedSearch) {
        return true;
      }
      const cvrSearchValues = getCustomerCvrLinks(customer)
        .flatMap((link) => [link.cvrnummer, link.label])
        .filter(Boolean)
        .join(' ');
      const labelSearchValues = getCustomerLabels(customer)
        .map((label) => label.name)
        .filter(Boolean)
        .join(' ');
      const haystack = [
        customer.company_name,
        customer.cvrnummer,
        customer.primary_contact_name,
        customer.primary_contact_email,
        customer.primary_contact_phone,
        cvrSearchValues,
        labelSearchValues
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(normalizedSearch.toLowerCase());
    });
  }, [customers, ownerFilter, searchTerm]);

  const filteredAnnualValue = useMemo(
    () =>
      filteredCustomers.reduce((sum, customer) => {
        const value = Number(customer.annual_value ?? 0);
        return Number.isFinite(value) ? sum + value : sum;
      }, 0),
    [filteredCustomers]
  );

  const statusCounts = useMemo(
    () =>
      customers.reduce(
        (acc, customer) => {
          acc[customer.status] += 1;
          return acc;
        },
        { ACTIVE: 0, ONBOARDING: 0, AT_RISK: 0, INACTIVE: 0 } as Record<Customer['status'], number>
      ),
    [customers]
  );

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedCustomer(null);
  }, []);

  const drawerCvrLinks = selectedCustomer ? getCustomerCvrLinks(selectedCustomer) : [];
  const primaryDrawerLink = drawerCvrLinks[0];

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchInput(value);
    if (value === '') {
      setSearchTerm('');
    }
  }, []);

  const handleSearch = useCallback((value: string) => {
    setSearchInput(value);
    setSearchTerm(value.trim().toLowerCase());
  }, []);

  const loadAvailableInsuranceTypes = useCallback(async () => {
    setInsuranceTypesLoading(true);
    try {
      const res = await api('/api/insurance-types');
      const data = (await res.json().catch(() => [])) as InsuranceType[];
      if (!res.ok) {
        throw new Error((data as any)?.error || 'Kunne ikke hente forsikringstyper');
      }
      const normalized = Array.isArray(data) ? data : [];
      setAvailableInsuranceTypes(
        [...normalized].sort((a, b) => a.title.localeCompare(b.title, 'da', { sensitivity: 'base' }))
      );
    } catch (error: any) {
      console.error('Failed to load insurance types', error);
      message.error(error?.message || 'Kunne ikke hente forsikringstyper');
    } finally {
      setInsuranceTypesLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAvailableInsuranceTypes();
  }, [loadAvailableInsuranceTypes]);

  const loadCustomerInsuranceTypes = useCallback(
    async (customerParam?: Customer | null) => {
      const customer = customerParam ?? selectedCustomer;
      if (!customer) {
        setCustomerInsuranceTypes([]);
        setSelectedInsuranceTypeIds([]);
        return;
      }

      const maybeUsePayload = (payload: any) => {
        const items = extractInsuranceTypes(payload);
        if (!items.length) return false;
        const normalized = normalizeInsuranceTypes(items);
        setCustomerInsuranceTypes(
          normalized.sort((a, b) => a.title.localeCompare(b.title, 'da', { sensitivity: 'base' }))
        );
        setSelectedInsuranceTypeIds(normalized.map((item) => String(item.insuranceTypeId)));
        return true;
      };

      // If the customer payload already contains insurance_types, use it immediately to render in the drawer
      const inlineTypes = extractInsuranceTypes(customer as any);
      if (inlineTypes.length > 0) {
        const normalizedInline = normalizeInsuranceTypes(inlineTypes);
        setCustomerInsuranceTypes(
          normalizedInline.sort((a, b) => a.title.localeCompare(b.title, 'da', { sensitivity: 'base' }))
        );
        setSelectedInsuranceTypeIds(normalizedInline.map((item) => String(item.insuranceTypeId)));
        return;
      }

      // Try fetching fresh customer by id (contains insurance_types)
      try {
        const res = await api(`/api/customers/${customer.id}`);
        if (res.ok) {
          const fresh = await res.json();
          if (maybeUsePayload(fresh)) {
            return;
          }
        }
      } catch (error) {
        console.warn('Failed to fetch customer detail by id', error);
      }

      // Fallback: try by-cvr if available (also includes insurance_types)
      const normalizedCvr = customer.cvrnummer ? customer.cvrnummer.replace(/\D+/g, '') : '';
      if (normalizedCvr) {
        try {
          const res = await api(`/api/customers/by-cvr/${normalizedCvr}`);
          if (res.ok) {
            const fresh = await res.json();
            if (maybeUsePayload(fresh)) {
              return;
            }
          }
        } catch (error) {
          console.warn('Failed to fetch customer detail by CVR', error);
        }
      }

      setInsuranceTypesLoading(true);
      try {
        const res = await api(`/api/customers/${customer.id}/insurance-types`);
        const data = (await res.json().catch(() => [])) as CustomerInsuranceType[];
        if (!res.ok) {
          throw new Error((data as any)?.error || 'Kunne ikke hente forsikringstyper for kunde');
        }
        const normalized = Array.isArray(data)
          ? data.map((item) => ({
              ...item,
              insuranceTypeId: String(item.insuranceTypeId ?? (item as any)?.insurancetypeid ?? '')
            }))
          : [];
        setCustomerInsuranceTypes(
          normalized.sort((a, b) => a.title.localeCompare(b.title, 'da', { sensitivity: 'base' }))
        );
        setSelectedInsuranceTypeIds(normalized.map((item) => String(item.insuranceTypeId)));
      } catch (error: any) {
        console.error('Failed to load customer insurance types', error);
        message.error(error?.message || 'Kunne ikke hente forsikringstyper for kunde');
      } finally {
        setInsuranceTypesLoading(false);
      }
    },
    [selectedCustomer]
  );

  const openCustomerDetail = useCallback(
    async (customer: Customer) => {
      setSelectedCustomer(customer);
      setDrawerOpen(true);
      await loadCustomerInsuranceTypes(customer);
    },
    [loadCustomerInsuranceTypes]
  );

  useEffect(() => {
    if (!drawerOpen || !selectedCustomer) return;
    void loadCustomerInsuranceTypes(selectedCustomer);
  }, [drawerOpen, selectedCustomer, loadCustomerInsuranceTypes]);

  const handleOpenInsuranceModal = useCallback(() => {
    setSelectedInsuranceTypeIds(customerInsuranceTypes.map((item) => String(item.insuranceTypeId)));
    setInsuranceModalOpen(true);
  }, [customerInsuranceTypes]);

  const handleCloseInsuranceModal = useCallback(() => {
    setInsuranceModalOpen(false);
  }, []);

  const handleToggleInsuranceType = useCallback((id: string, checked: boolean) => {
    setSelectedInsuranceTypeIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return Array.from(next);
    });
  }, []);

  const handleSaveInsuranceTypes = useCallback(async () => {
    if (!selectedCustomer) {
      return;
    }
    setInsuranceModalSaving(true);
    try {
      const res = await api(`/api/customers/${selectedCustomer.id}/insurance-types`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ insuranceTypeIds: selectedInsuranceTypeIds })
      });
      const data = (await res.json().catch(() => ({}))) as any;
      if (!res.ok) {
        throw new Error(data?.error || 'Kunne ikke opdatere forsikringstyper');
      }
      message.success('Forsikringstyper opdateret');
      setInsuranceModalOpen(false);
      await loadCustomerInsuranceTypes();
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke opdatere forsikringstyper');
    } finally {
      setInsuranceModalSaving(false);
    }
  }, [loadCustomerInsuranceTypes, selectedCustomer, selectedInsuranceTypeIds]);

  const handleRemoveInsuranceType = useCallback(async (insuranceTypeId: string) => {
    if (!selectedCustomer) {
      return;
    }
    setRemovingInsuranceTypeId(insuranceTypeId);
    try {
      const remainingIds = customerInsuranceTypes
        .map((item) => String(item.insuranceTypeId))
        .filter((id) => id !== insuranceTypeId);
      const res = await api(`/api/customers/${selectedCustomer.id}/insurance-types`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ insuranceTypeIds: remainingIds })
      });
      const data = (await res.json().catch(() => ({}))) as any;
      if (!res.ok) {
        throw new Error(data?.error || 'Kunne ikke fjerne forsikringstype');
      }
      message.success('Forsikringstype fjernet');
      await loadCustomerInsuranceTypes();
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke fjerne forsikringstype');
    } finally {
      setRemovingInsuranceTypeId(null);
    }
  }, [customerInsuranceTypes, loadCustomerInsuranceTypes, selectedCustomer]);

  const columns: ColumnsType<Customer> = useMemo(
    () => [
      {
        title: 'Virksomhed',
        key: 'company',
        render: (_text, record) => {
          const links = getCustomerCvrLinks(record);
          const labels = getCustomerLabels(record);
          return (
            <Space direction="vertical" size={2}>
              <span style={{ fontWeight: 600 }}>{record.company_name}</span>
              {labels.length > 0 ? (
                <Space size={4} wrap>
                  {labels.map((label) => (
                    <Tag key={`${record.id}-label-${label.id}`} color={label.color || 'default'}>
                      {label.name}
                    </Tag>
                  ))}
                </Space>
              ) : null}
              {links.map((link, index) => {
                if (!link.cvrnummer) {
                  return null;
                }
                const label = link.label && link.label.trim() ? `${link.label.trim()} · ` : '';
                return (
                  <Text type="secondary" key={link.id || `${record.id}-cvr-${index}`}>
                    {label}CVR {formatCvrNumber(link.cvrnummer)}
                  </Text>
                );
              })}
            </Space>
          );
        }
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (value: Customer['status']) => (
          <Tag color={statusColors[value] || 'default'}>{statusLabels[value] || value}</Tag>
        ),
        width: 140
      },
      {
        title: 'Kontrakt',
        key: 'contract',
        render: (_text, record) => {
          const links = getCustomerCvrLinks(record);
          return (
            <Space direction="vertical" size={links.length > 1 ? 8 : 0}>
              {links.map((link, index) => (
                <Space direction="vertical" size={0} key={link.id || `${record.id}-contract-${index}`}>
                  {links.length > 1 ? <Text strong>{formatCvrLabel(link, index)}</Text> : null}
                  <Text type="secondary">Underskrevet: {formatDate(link.contract_signed_at)}</Text>
                  <Text type="secondary">Start: {formatDate(link.contract_starts_at)}</Text>
                  <Text type="secondary">Udløb: {formatDate(link.contract_expires_at)}</Text>
                </Space>
              ))}
            </Space>
          );
        },
        width: 240
      },
      {
        title: 'Fornyelse',
        key: 'renewal',
        render: (_text, record) => {
          const links = getCustomerCvrLinks(record);
          return (
            <Space direction="vertical" size={links.length > 1 ? 8 : 0}>
              {links.map((link, index) => (
                <Space direction="vertical" size={0} key={link.id || `${record.id}-renewal-${index}`}>
                  {links.length > 1 ? <Text strong>{formatCvrLabel(link, index)}</Text> : null}
                  <Text type="secondary">Varsling: {formatDate(link.renewal_notice_at)}</Text>
                  <Text type="secondary">
                    Værdi: {formatAmount(link.annual_value, link.currency || record.currency || undefined)}
                  </Text>
                </Space>
              ))}
            </Space>
          );
        },
        width: 220
      },
      {
        title: 'Provision',
        key: 'commission',
        render: (_text, record) => {
          const links = getCustomerCvrLinks(record);
          return (
            <Space direction="vertical" size={links.length > 1 ? 8 : 0}>
              {links.map((link, index) => (
                <Space direction="vertical" size={0} key={link.id || `${record.id}-commission-${index}`}>
                  {links.length > 1 ? <Text strong>{formatCvrLabel(link, index)}</Text> : null}
                  <Text type="secondary">
                    {formatProvisionSummary(
                      link.commission_percent,
                      link.commission_amount,
                      link.currency || record.currency || undefined
                    )}
                  </Text>
                </Space>
              ))}
            </Space>
          );
        },
        width: 160
      },
      {
        title: 'Account owner',
        key: 'owner',
        render: (_text, record) => ownerLabel(record),
        width: 200
      },
      {
        title: 'Forsikringstyper',
        key: 'insuranceTypes',
        render: (_text, record) => {
          const inline = normalizeInsuranceTypes(extractInsuranceTypes(record as any));
          if (!inline.length) {
            return <Text type="secondary">—</Text>;
          }
          return (
            <Space direction="vertical" size={4}>
              {inline.slice(0, 3).map((item) => (
                <span key={item.insuranceTypeId}>{item.title || item.insuranceTypeId}</span>
              ))}
              {inline.length > 3 ? <Text type="secondary">+ {inline.length - 3} flere</Text> : null}
            </Space>
          );
        }
      },
      {
        title: 'Kontakt',
        key: 'contact',
        render: (_text, record) => buildContactSummary(record)
      }
    ],
    []
  );

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Statistic title="Hentede kunder" value={customers.length} />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Statistic title="Viste kunder" value={filteredCustomers.length} />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Statistic
            title="Årlig værdi (vises)"
            value={filteredAnnualValue}
            formatter={(value) => formatAmount(value as number, 'DKK')}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Statistic title="Kunder i risiko" value={statusCounts.AT_RISK} />
        </Col>
      </Row>

      <Card title="Kundeoversigt">
        <Row gutter={[16, 16]} align="middle" style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <Select
              value={statusFilter}
              onChange={(value) => setStatusFilter(value as StatusFilter)}
              options={statusFilterOptions}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              value={ownerFilter}
              onChange={(value) => setOwnerFilter(value as OwnerFilter)}
              options={ownerOptions}
              optionFilterProp="label"
              showSearch
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Input.Search
              placeholder="Søg efter virksomhedsnavn, CVR eller kontakt"
              allowClear
              value={searchInput}
              onChange={handleSearchChange}
              onSearch={handleSearch}
              enterButton="Søg"
            />
          </Col>
          <Col xs={24} sm={12} md={4} style={{ textAlign: 'right' }}>
            <Button icon={<ReloadOutlined />} onClick={() => void loadCustomers()} loading={loading}>
              Opdater
            </Button>
          </Col>
        </Row>

        <Table
          dataSource={filteredCustomers}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          locale={{ emptyText: <Empty description="Ingen kunder matchede filteret" /> }}
          onRow={(record) => ({
            onClick: () => openCustomerDetail(record),
            style: { cursor: 'pointer' }
          })}
          size="middle"
          scroll={{ x: 1100 }}
        />
      </Card>

      <Drawer
        title={selectedCustomer?.company_name || 'Kundedetaljer'}
        open={drawerOpen}
        onClose={closeDrawer}
        width={520}
        destroyOnClose
      >
        {selectedCustomer ? (
          <>
            <Space size="small" style={{ marginBottom: 16 }}>
              <Tag color={statusColors[selectedCustomer.status] || 'default'}>
                {statusLabels[selectedCustomer.status] || selectedCustomer.status}
              </Tag>
              {getCustomerLabels(selectedCustomer).map((label) => (
                <Tag key={`${selectedCustomer.id}-drawer-label-${label.id}`} color={label.color || 'default'}>
                  {label.name}
                </Tag>
              ))}
              <Text type="secondary">
                CVR {primaryDrawerLink?.cvrnummer ? formatCvrNumber(primaryDrawerLink.cvrnummer) : '—'}
              </Text>
            </Space>
            <Descriptions column={1} size="small" colon={false}>
              <Descriptions.Item label="Account owner">{ownerLabel(selectedCustomer)}</Descriptions.Item>
              <Descriptions.Item label="Kontakt">{buildContactSummary(selectedCustomer)}</Descriptions.Item>
            </Descriptions>
            <div style={{ marginTop: 24 }}>
              <Title level={5} style={{ marginBottom: 8 }}>
                CVR og kontrakter
              </Title>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {drawerCvrLinks.map((link, index) => (
                  <Card
                    size="small"
                    key={link.id || `${selectedCustomer.id}-drawer-cvr-${index}`}
                    title={formatCvrLabel(link, index)}
                    extra={
                      <Tag color={statusColors[link.status] || 'default'}>
                        {statusLabels[link.status] || link.status}
                      </Tag>
                    }
                  >
                    <Descriptions column={1} size="small" colon={false}>
                      <Descriptions.Item label="CVR">
                        {link.cvrnummer ? formatCvrNumber(link.cvrnummer) : '—'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Underskrevet">
                        {formatDate(link.contract_signed_at)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Start">
                        {formatDate(link.contract_starts_at)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Udløb">
                        {formatDate(link.contract_expires_at)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Fornyelse">
                        {formatDate(link.renewal_notice_at)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Årlig værdi">
                        {formatAmount(link.annual_value, link.currency || selectedCustomer.currency || undefined)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Provision">
                        {formatProvisionSummary(
                          link.commission_percent,
                          link.commission_amount,
                          link.currency || selectedCustomer.currency || undefined
                        )}
                      </Descriptions.Item>
                    </Descriptions>
                    {link.notes ? (
                      <Paragraph style={{ marginTop: 8 }}>{link.notes}</Paragraph>
                    ) : null}
                  </Card>
                ))}
              </Space>
            </div>
            <div style={{ marginTop: 24 }}>
              <Card
                size="small"
                title="Forsikringstyper"
                loading={insuranceTypesLoading}
                extra={
                  <Button
                    icon={<PlusOutlined />}
                    size="small"
                    type="primary"
                    onClick={handleOpenInsuranceModal}
                    disabled={!selectedCustomer || availableInsuranceTypes.length === 0}
                  >
                    Tilføj / rediger
                  </Button>
                }
              >
                {displayInsuranceTypes.length === 0 ? (
                  <Text type="secondary">Ingen forsikringstyper tilknyttet.</Text>
                ) : (
                  <List
                    size="small"
                    dataSource={displayInsuranceTypes}
                    renderItem={(item) => (
                      <List.Item
                        key={item.id || `${selectedCustomer?.id}-ins-${item.insuranceTypeId}`}
                        actions={[
                          <Button
                            key="remove"
                            type="text"
                            danger
                            size="small"
                            onClick={() => handleRemoveInsuranceType(String(item.insuranceTypeId))}
                            loading={removingInsuranceTypeId === String(item.insuranceTypeId)}
                          >
                            Fjern
                          </Button>
                        ]}
                      >
                        <List.Item.Meta
                          title={
                            <Space size={8} wrap>
                              <Text strong>{item.title || item.insuranceTypeId}</Text>
                              {item.category ? <Tag>{item.category}</Tag> : null}
                              {item.mandatory ? <Tag color="red">Lovpligtig</Tag> : null}
                            </Space>
                          }
                          description={
                            item.description ? (
                              <Text>{item.description}</Text>
                            ) : (
                              <Text type="secondary">Ingen beskrivelse</Text>
                            )
                          }
                        />
                      </List.Item>
                    )}
                  />
                )}
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Debug: inline {inlineInsuranceTypes.length}, fetched {customerInsuranceTypes.length}, display {displayInsuranceTypes.length}
                  {!!selectedCustomer && ' · raw insurance_types ' + JSON.stringify((selectedCustomer as any).insurance_types)}
                </Text>
                {availableInsuranceTypes.length === 0 && !insuranceTypesLoading ? (
                  <>
                    <Divider style={{ margin: '12px 0' }} />
                    <Text type="secondary">
                      Opret forsikringstyper under Tilpasning for at tilføje dem til kunder.
                    </Text>
                  </>
                ) : null}
              </Card>
            </div>
            {selectedCustomer.notes ? (
              <div style={{ marginTop: 24 }}>
                <Title level={5} style={{ marginBottom: 8 }}>
                  Noter
                </Title>
                <Paragraph>{selectedCustomer.notes}</Paragraph>
              </div>
            ) : null}
          </>
        ) : null}
      </Drawer>

      <Modal
        title="Tilknyt forsikringstyper"
        open={insuranceModalOpen}
        onCancel={handleCloseInsuranceModal}
        onOk={handleSaveInsuranceTypes}
        okText="Gem"
        confirmLoading={insuranceModalSaving}
        destroyOnClose
        width={640}
      >
        <List
          dataSource={availableInsuranceTypes}
          loading={insuranceTypesLoading}
          locale={{ emptyText: 'Ingen forsikringstyper oprettet endnu' }}
          renderItem={(item) => (
            <List.Item key={item.id}>
              <List.Item.Meta
                title={
                  <Space size={8} wrap>
                    <Checkbox
                      checked={selectedInsuranceTypeIds.includes(item.id)}
                      onChange={(e) => handleToggleInsuranceType(item.id, e.target.checked)}
                    >
                      {item.title}
                    </Checkbox>
                    {item.category ? <Tag>{item.category}</Tag> : null}
                    {item.mandatory ? <Tag color="red">Lovpligtig</Tag> : null}
                  </Space>
                }
                description={
                  item.description ? (
                    <Text>{item.description}</Text>
                  ) : (
                    <Text type="secondary">Ingen beskrivelse</Text>
                  )
                }
              />
            </List.Item>
          )}
        />
      </Modal>
    </Space>
  );
}
